import {Workspace} from '@app/gen/schemas/workspace';
import {auth} from '@common/auth/use-auth';
import {getCookie, setCookie} from '@ui/utils/hooks/use-cookie';
import {create} from 'zustand';
import {subscribeWithSelector} from 'zustand/middleware';

type WorkspaceState = {
  workspaces: Workspace[];
  activeWorkspace: Workspace | null;
  setActiveWorkspace: (id: number) => void;
  setPersonalAsActive: () => void;
  setWorkspaces: (workspaces: Workspace[]) => void;
  init: (workspaces: Workspace[]) => void;
};

export const useWorkspaceStore = create<WorkspaceState>()(
  subscribeWithSelector((set, get) => ({
    workspaces: [],
    activeWorkspace: null,
    setActiveWorkspace: id => {
      if (id === get().activeWorkspace?.id) return;

      const workspaces = get().workspaces;
      const workspace = workspaces.find(workspace => workspace.id === id);

      // Do not fall back to Personal — that looked like the picker snapped back.
      if (!workspace) return;

      setCookie(workspaceCookieName(), `${workspace.id}`, {
        days: 365,
        path: '/',
        SameSite: 'Lax',
      });
      set({activeWorkspace: workspace});
    },
    setPersonalAsActive: () => {
      const personalWorkspace = get().workspaces.find(
        workspace => workspace.is_personal,
      );
      if (personalWorkspace) {
        get().setActiveWorkspace(personalWorkspace.id);
      }
    },
    setWorkspaces: (workspaces: Workspace[]) => {
      const activeId = get().activeWorkspace?.id;
      const activeWorkspace = activeId
        ? (workspaces.find(workspace => workspace.id === activeId) ??
          get().activeWorkspace)
        : get().activeWorkspace;
      set({workspaces, activeWorkspace});
    },
    init: incoming => {
      const workspaces = Array.isArray(incoming) ? incoming : [];
      const params = new URLSearchParams(window.location.search);
      const fromUrl = params.get('workspace_id');
      const cookieId = getCookie(workspaceCookieName());
      let activeWorkspace: Workspace | undefined;

      if (fromUrl) {
        activeWorkspace = workspaces.find(
          workspace => `${workspace.id}` === `${fromUrl}`,
        );
      } else if (cookieId) {
        activeWorkspace = workspaces.find(
          workspace => `${workspace.id}` === `${cookieId}`,
        );
      }

      if (!activeWorkspace) {
        activeWorkspace =
          workspaces.find(workspace => workspace.is_personal) ?? workspaces[0];
      }

      if (activeWorkspace) {
        setCookie(workspaceCookieName(), `${activeWorkspace.id}`, {
          days: 365,
          path: '/',
          SameSite: 'Lax',
        });
      }

      set({workspaces, activeWorkspace: activeWorkspace ?? null});
    },
  })),
);

// make sure cookie is unique to the account user is currently logged in with,
// so that logging in to another account doesn't affect the workspace cookie
function workspaceCookieName(): string {
  return `workspace_id_${auth.user!.id}`;
}

type WorkspaceCounts = Workspace & {root_count?: number};

export function workspaceFileCount(workspace?: Workspace | null): number {
  const row = workspace as WorkspaceCounts | undefined;
  if (row?.root_count != null) {
    return Number(row.root_count);
  }
  return Number(row?.files_count ?? 0);
}

export function workspaceTotalFiles(workspace?: Workspace | null): number {
  return Number(workspace?.files_count ?? 0);
}

export function preferWorkspaceWithFiles(
  workspaces: Workspace[],
  current?: Workspace,
): Workspace | undefined {
  const richest = [...workspaces].sort((a, b) => {
    const byFiles = workspaceTotalFiles(b) - workspaceTotalFiles(a);
    if (byFiles !== 0) {
      return byFiles;
    }
    return workspaceFileCount(b) - workspaceFileCount(a);
  })[0];

  if (
    current &&
    (workspaceFileCount(current) > 0 || workspaceTotalFiles(current) > 0)
  ) {
    return current;
  }

  return (
    richest ??
    current ??
    workspaces.find(workspace => workspace.is_personal)
  );
}
